import { createFileRoute, Navigate } from '@tanstack/react-router';

import { AutoAssignEligibilityCard } from '@apps/web/components/settings/auto_assign_eligibility_card';
import { ClientNotificationsSection } from '@apps/web/components/settings/client_notifications_section';
import { FillingOpenShiftsCard } from '@apps/web/components/settings/filling_open_shifts_card';
import { OpenShiftResponsesSection } from '@apps/web/components/settings/open_shift_responses_section';
import { CarePlanSettingsCard } from '@apps/web/components/settings/outreach_settings_card';
import { OutreachTemplatesCard } from '@apps/web/components/settings/outreach_templates_card';
import { SettingsPanel } from '@apps/web/components/settings/settings_primitives';
import { ShiftConfirmationAutoReplyCard } from '@apps/web/components/settings/shift_confirmation_auto_reply_card';
import {
    SettingsPageSection,
    SettingsPageSections,
    SettingsPageShell,
    SettingsSection,
    useSettingsGates,
} from '@apps/web/features/settings';
import { useFeatureFlag } from '@apps/web/lib/feature_flag_overrides';
import { useOrganization } from '@apps/web/swr_data_fetching/data_fetching_hooks';

export const Route = createFileRoute('/_app/settings/outreach-automation')({
    component: SchedulingPage,
});

function SchedulingPage() {
    const { organizationId, gates } = useSettingsGates();
    const { data: organization } = useOrganization(organizationId, {
        reason: 'settings',
    });
    const autoAssignEligibilityEnabled = useFeatureFlag(
        'auto_assign_eligibility_enabled',
        organization?.features,
    );
    if (!gates.canManageOrgSettings) {
        return <Navigate to="/settings" replace={true} />;
    }
    if (organizationId === null) {
        return null;
    }
    const clientNotificationsEnabled =
        !!organization?.features?.client_notification_enabled;

    return (
        <SettingsPageShell
            title="Scheduling"
            description="How Phoebe fills open shifts, replies to caregivers, and keeps clients in the loop."
        >
            <SettingsPageSections>
                <SettingsPageSection
                    title="Filling open shifts"
                    description="Who Phoebe reaches out to, and how it drafts and groups open-shift outreach."
                >
                    <div className="space-y-6">
                        <FillingOpenShiftsCard
                            organizationId={organizationId}
                        />
                        {autoAssignEligibilityEnabled ? (
                            <AutoAssignEligibilityCard
                                organizationId={organizationId}
                            />
                        ) : null}
                    </div>
                </SettingsPageSection>
                <SettingsPageSection
                    title="Caregiver replies"
                    description="How Phoebe responds when caregivers text back — for open-shift outreach and shift confirmations."
                >
                    <div className="space-y-6">
                        <SettingsSection
                            variant="flat"
                            title="Open-shift responses"
                        >
                            <OpenShiftResponsesSection
                                organizationId={organizationId}
                                variant="card"
                            />
                        </SettingsSection>
                        <SettingsSection
                            variant="flat"
                            title="Shift-confirmation replies"
                        >
                            <ShiftConfirmationAutoReplyCard
                                organizationId={organizationId}
                                variant="card"
                            />
                        </SettingsSection>
                    </div>
                </SettingsPageSection>
                {clientNotificationsEnabled ? (
                    <SettingsPageSection
                        title="Client notifications"
                        description="Calls Phoebe places to clients once a shift is filled."
                    >
                        <ClientNotificationsSection
                            organizationId={organizationId}
                        />
                    </SettingsPageSection>
                ) : null}
                <OutreachTemplatesCard
                    organizationId={organizationId}
                    outreachTypes={['callout', 'recurring_shift']}
                    title="Message templates"
                    description="The SMS templates Phoebe uses for outreach, fill-shift results, and completion updates."
                    variant="embedded"
                    includeSchedulingResultTemplates={true}
                />
                <SettingsPageSection
                    title="Care Plan Summary"
                    description="The care-plan context Phoebe uses on calls and texts."
                >
                    <SettingsPanel bodyClassName="p-5">
                        <CarePlanSettingsCard
                            organizationId={organizationId}
                            hideTitle={true}
                        />
                    </SettingsPanel>
                </SettingsPageSection>
            </SettingsPageSections>
        </SettingsPageShell>
    );
}
